import type { AxiosResponse } from "axios";

import { HttpApiService } from "@/shared/services/httpApi.service";
import type { FeeBreakdownTaxSnapshot } from "@/shared/utils/paymentTaxes";
import { normalizePixKey } from "@/shared/utils/pixKey";

/**
 * Integration with the NEW NestJS payments runtime (payin/payout), NOT the legacy PHP dispersions proxy.
 * These endpoints live under the internal API (`/internal/payments/*`, JWT, permission `payments:write`) and
 * execute the payout directly here (provider adapter, e.g. Bitso) instead of PHP.
 *
 * Base URL note: `HttpApiService` already targets the NestJS API (its base URL includes `/api/v1`), the same
 * client used for `/auth/*`. So the JWT is attached automatically.
 *
 * Backend contract: `POST /internal/payments/payouts` → `CreatePayoutDto` (see coloca-nest
 * `src/coloca/payments/dto/create-payout.dto.ts`).
 */

// Rails supported by the runtime today (SPEI + PIX + CBU_CVU + BREB). Brazil uses
// PIX, Mexico SPEI, Argentina CBU_CVU, Colombia BREB (Bre-B).
// `const ... as const` + union type (not `enum`) — the project enables TS
// `erasableSyntaxOnly`, which forbids runtime-emitting enums.
export const PayoutRail = {
  PIX: "PIX",
  SPEI: "SPEI",
  CBU_CVU: "CBU_CVU",
  BREB: "BREB",
  TRANSFIYA: "TRANSFIYA",
} as const;
export type PayoutRail = (typeof PayoutRail)[keyof typeof PayoutRail];

// Mirrors coloca-nest `TransfiyaAccountType` — ACH en línea bank-account types
// (ISO 20022-style codes used in the network's accountType:accountNumber target).
export const TransfiyaAccountType = {
  SAVINGS: "svgs",
  CHECKING: "cacc",
  DEPOSIT: "dbmo",
  STANDARD_DEPOSIT: "dord",
  INCLUSIVE_DEPOSIT: "dbmi",
} as const;
export type TransfiyaAccountType = (typeof TransfiyaAccountType)[keyof typeof TransfiyaAccountType];

// Mirrors coloca-nest `ColombianDocumentType` (subset the dispersion form offers).
export const ColombianDocumentType = {
  CC: "CC",
  CE: "CE",
  NIT: "NIT",
  TI: "TI",
  PA: "PA",
  PPT: "PPT",
} as const;
export type ColombianDocumentType = (typeof ColombianDocumentType)[keyof typeof ColombianDocumentType];

// Mirrors coloca-nest `BrebKeyType` — the Bre-B key (alias) types registered in
// Colombia's DICE directory. Natural persons hold PHONE/EMAIL/ALPHANUM/NRIC;
// MERCHANT codes identify businesses.
export const BrebKeyType = {
  PHONE: "PHONE",
  EMAIL: "EMAIL",
  ALPHANUM: "ALPHANUM",
  NRIC: "NRIC",
  MERCHANT: "MERCHANT",
} as const;
export type BrebKeyType = (typeof BrebKeyType)[keyof typeof BrebKeyType];

// Mirrors the backend `CbuCvuDestinationDto.account_id_type` gate (ALIAS not supported yet).
export const CbuCvuAccountIdType = {
  CBU: "CBU",
  CVU: "CVU",
} as const;
export type CbuCvuAccountIdType = (typeof CbuCvuAccountIdType)[keyof typeof CbuCvuAccountIdType];

/**
 * Which Argentine account id the form is describing, or `null` for a value that is neither.
 *
 * **An absent value means CVU, and that is not a guess**: the step-2 select shows CVU when the field is
 * unset, and the payout is dispatched with the same default, so an untouched form genuinely pays a CVU. What
 * must not be defaulted is a value that is _present and unrecognised_ — the number is 22 digits either way,
 * so nothing about it says which, and `buildArgentinaCbuCvuPayoutPayload` throws on it rather than pay the
 * wrong rail.
 *
 * It exists because that default was written three times — the select's display, the payout payload and the
 * saved-account draft — each as its own ternary. Three copies of a default is how an account ends up stored
 * under a different field than the one that was actually paid, and nothing reports it.
 */
export function resolveCbuCvuKind(raw: unknown): CbuCvuAccountIdType | null {
  const value = String(raw ?? CbuCvuAccountIdType.CVU)
    .trim()
    .toUpperCase();
  return value === CbuCvuAccountIdType.CBU || value === CbuCvuAccountIdType.CVU ? value : null;
}

// Mirrors coloca-nest `PixKeyType`.
export const PixKeyType = {
  CPF: "CPF",
  CNPJ: "CNPJ",
  EMAIL: "EMAIL",
  PHONE: "PHONE",
  EVP: "EVP",
} as const;
export type PixKeyType = (typeof PixKeyType)[keyof typeof PixKeyType];

// Mirrors coloca-nest `PayoutStatus`.
export const PayoutStatus = {
  CREATED: "CREATED",
  PROCESSING: "PROCESSING",
  PAID: "PAID",
  FAILED: "FAILED",
  CANCELLED: "CANCELLED",
  REVERSED: "REVERSED",
} as const;
export type PayoutStatus = (typeof PayoutStatus)[keyof typeof PayoutStatus];

// Discriminated destination union — matches the backend `oneOf` on `rail`.
export interface PixPayoutDestination {
  rail: "PIX";
  pix_key_type: PixKeyType;
  pix_key_value: string;
  beneficiary_name?: string;
}

export interface SpeiPayoutDestination {
  rail: "SPEI";
  clabe: string;
  beneficiary_name: string;
  rfc?: string;
}

export interface CbuCvuPayoutDestination {
  rail: "CBU_CVU";
  account_id_type: CbuCvuAccountIdType;
  /** 22-digit CBU (bank) or CVU (wallet). */
  account_id_value: string;
  /** Beneficiary CUIT/CUIL (<= 13 chars). */
  cuit_cuil?: string;
  beneficiary_name?: string;
}

export interface BrebPayoutDestination {
  rail: "BREB";
  breb_key_type: BrebKeyType;
  /** Bre-B key value (<= 64 chars) — forwarded verbatim to the provider's directory resolution. */
  breb_key_value: string;
  beneficiary_name?: string;
}

export interface TransfiyaPayoutDestination {
  rail: "TRANSFIYA";
  account_type: TransfiyaAccountType;
  /** Destination bank-account number (digits, <= 34). */
  account_number: string;
  /** Destination bank participant code (from GET /internal/payments/transfiya-banks). */
  bank_code: string;
  document_type: ColombianDocumentType;
  document_number: string;
  /** Required for TRANSFIYA — the receiving bank displays it. */
  beneficiary_name: string;
}

export type PayoutDestination =
  | PixPayoutDestination
  | SpeiPayoutDestination
  | CbuCvuPayoutDestination
  | BrebPayoutDestination
  | TransfiyaPayoutDestination;

/** Request body for `POST /internal/payments/payouts` (CreatePayoutDto). */
export interface CreatePayoutPayload {
  organization_id: string;
  rail: PayoutRail;
  country: string; // ISO 3166-1 alpha-3, e.g. "BRA"
  /** Delivery currency — what the beneficiary is paid in, e.g. "BRL". */
  currency: string;
  /**
   * The quote's `amounts.receive` — the CONVERTED GROSS the dispersion commission applies to, NOT
   * `amounts.payout_net` (what the beneficiary receives). Sent verbatim as a decimal string; the backend
   * re-derives it from the quote and rejects a mismatch, so this is a cross-check rather than the source of
   * truth.
   */
  gross_amount: string;
  /**
   * Quote to redeem. Required: the conversion (when the wallet currency differs from the corridor's) and both
   * fees live on it, and the backend charges what was quoted. Single-use — a second payout on the same quote
   * is a 409.
   */
  quote_id: string;
  idempotency_key: string; // <= 64 chars, unique per operation
  concept?: string; // <= 120 chars
  destination: PayoutDestination;
  /** Scanned Bre-B QR this payout pays (BREB only), exactly as the scanner returned it. */
  breb_qr?: { qr_string: string };
}

/** Subset of the returned PayoutEntity the UI cares about. */
export interface PayoutResponse {
  id: string;
  organization_id: string;
  rail: PayoutRail;
  country: string;
  currency: string;
  gross_amount: string;
  fee_total: string;
  net_amount: string;
  /** Only the taxes are read; members never receive the Coloca-borne ones. */
  fee_breakdown?: { taxes?: FeeBreakdownTaxSnapshot[] | null };
  status: PayoutStatus;
  provider_code: string | null;
  provider_reference: string | null;
  concept: string | null;
  idempotency_key: string;
  created_by_user_id: string | null;
  paid_at: string | null;
  failed_at: string | null;
  failure_reason: string | null;
  created_at: string;
  updated_at: string;
  /** `GET /payouts/:id` only: who a PAID Bre-B payout reached, as the network reported them. */
  beneficiary_name?: string | null;
  /** The wallet currency debited; differs from `currency` on a cross-currency payout. */
  source_currency?: string | null;
  /** `GET /payouts/:id` only: what the comprobante states beyond the row. */
  receipt?: PayoutReceiptData;
}

export type PayoutReceiptDestinationType =
  | "BREB_KEY"
  | "PIX_KEY"
  | "BANK_ACCOUNT"
  | "CLABE"
  | "CBU_CVU"
  | "CRYPTO_WALLET";

/** Coloca-nest `PayoutReceipt`. */
export interface PayoutReceiptData {
  /** A Bre-B holder is masked until the payout is PAID; any other rail carries the typed name. */
  beneficiary_name: string | null;
  bank_name: string | null;
  destination: { type: PayoutReceiptDestinationType; subtype: string | null; value: string } | null;
  /** A scanned Bre-B QR's own reference, or a USDT transfer's hash. */
  reference: string | null;
  /** The Bre-B network's transaction id, the same one the receiving bank shows. Absent on older backends. */
  network_reference?: string | null;
  /** The organization's own verified identity — who the receiver sees as the sender. */
  sender: { name: string; document_type: string | null; document_last4: string | null } | null;
}

/** Query params for the runtime payouts list — mirrors coloca-nest `PayoutsQueryDto` (all AND-combined). */
export interface ListPayoutsParams {
  /**
   * Tenancy: org-scoped callers MUST pass it (an unscoped query would return every org's payouts to an
   * INTERNAL user). Only INTERNAL staff views may omit it to list cross-org.
   */
  organizationId?: string | null;
  /** PayoutStatus enum value (single — the API does not accept multi-select). */
  status?: PayoutStatus;
  rail?: PayoutRail;
  /** ISO 3166-1 alpha-3, e.g. "MEX". */
  country?: string;
  /** E.g. "MXN". */
  currency?: string;
  /** Created_at >= from (ISO 8601). */
  from?: string;
  /** Created_at <= to (ISO 8601). */
  to?: string;
  /** Currency of the wallet debited; differs from `currency` only on a payout that converted. */
  sourceCurrency?: string;
  /** `completed` makes `from` / `to` (and the order) apply to `paid_at` instead of `created_at`. */
  dateField?: "created" | "completed";
  /** Page size — backend default 10, max 200. */
  limit?: number;
  /** Offset — backend default 0. */
  skip?: number;
}

/** Paginated shape of the runtime list endpoints (`PaginatedResponse<T>` in coloca-nest). */
export interface PaginatedRuntimePayouts {
  data: PayoutResponse[];
  total: number;
  skip: number;
  limit: number;
  hasMore: boolean;
}

// Legacy PIX key type selector (Step 2 config) stores a numeric index.
// Map it to the backend string enum. See countryFields.config Brasil.
const PIX_KEY_TYPE_BY_LEGACY_NUMBER: Record<number, PixKeyType> = {
  0: PixKeyType.CPF,
  1: PixKeyType.CNPJ,
  2: PixKeyType.EMAIL,
  3: PixKeyType.PHONE,
  4: PixKeyType.EVP,
};

/** Convert the form's numeric/string pix_type into the backend enum. */
export function toPixKeyType(value: number | string | null | undefined): PixKeyType {
  if (typeof value === "number") {
    const mapped = PIX_KEY_TYPE_BY_LEGACY_NUMBER[value];
    if (mapped) return mapped;
  }
  if (typeof value === "string") {
    const upper = value.trim().toUpperCase();
    if ((Object.values(PixKeyType) as string[]).includes(upper)) {
      return upper as PixKeyType;
    }
    const asNumber = Number(value);
    if (!Number.isNaN(asNumber) && PIX_KEY_TYPE_BY_LEGACY_NUMBER[asNumber]) {
      return PIX_KEY_TYPE_BY_LEGACY_NUMBER[asNumber];
    }
  }
  throw new Error(`Unsupported PIX key type: ${String(value)}`);
}

/**
 * Format a numeric amount into the backend money string (plain decimal, no thousands separator, up to 8 dp).
 * The quote returns amounts as `number`; the payout body needs a string.
 */
export function toMoneyString(amount: number | string, decimals = 2): string {
  const n = typeof amount === "string" ? Number(amount) : amount;
  if (!Number.isFinite(n)) throw new Error(`Invalid amount: ${String(amount)}`);
  return n.toFixed(decimals);
}

/**
 * Normalize the PIX key for the API. The Step 2 input stores the DISPLAY format (CPF "000.000.000-00", phone
 * "(11) 98765-4321"), but the backend forwards `pix_key_value` verbatim to the provider (Bitso), which
 * expects the raw key — delegates to the shared normalizer so account storage and payout transport stay
 * byte-identical (the local `PixKeyType` values are the same string literals as `PixKeyTypeEnum`).
 */
export function normalizePixKeyValue(type: PixKeyType, key: string): string {
  return normalizePixKey(type, key);
}

/** New idempotency key per payout attempt (retries must reuse the same key). */
export function newIdempotencyKey(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  // Fallback for environments without crypto.randomUUID.
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export interface BuildBrazilPixPayoutInput {
  organizationId: string;
  /** The quote's converted gross in the corridor currency, decimal string. */
  receiveAmount: string;
  /** Quote being redeemed. */
  quoteId: string;
  pixKeyType: number | string | PixKeyType;
  pixKeyValue: string;
  beneficiaryName?: string;
  concept?: string;
  /** Reuse across retries of the same operation. Defaults to a fresh uuid. */
  idempotencyKey?: string;
}

/**
 * Build the CreatePayoutPayload for the Brazil / PIX corridor from the redeemed quote. `gross_amount` is the
 * quote's converted gross (`amounts.receive`); the backend then deducts the dispersion commission from it, so
 * `net_amount` is deliberately lower and is what the beneficiary receives (`amounts.payout_net`).
 */
export function buildBrazilPixPayoutPayload(input: BuildBrazilPixPayoutInput): CreatePayoutPayload {
  const pixKeyType =
    typeof input.pixKeyType === "string" && (Object.values(PixKeyType) as string[]).includes(input.pixKeyType)
      ? (input.pixKeyType as PixKeyType)
      : toPixKeyType(input.pixKeyType);
  return {
    organization_id: input.organizationId,
    rail: PayoutRail.PIX,
    country: "BRA",
    currency: "BRL",
    gross_amount: input.receiveAmount,
    quote_id: input.quoteId,
    idempotency_key: input.idempotencyKey ?? newIdempotencyKey(),
    ...(input.concept ? { concept: input.concept } : {}),
    destination: {
      rail: PayoutRail.PIX,
      pix_key_type: pixKeyType,
      pix_key_value: normalizePixKeyValue(pixKeyType, input.pixKeyValue),
      ...(input.beneficiaryName ? { beneficiary_name: input.beneficiaryName } : {}),
    },
  };
}

export interface BuildMexicoSpeiPayoutInput {
  organizationId: string;
  /** The quote's converted gross in the corridor currency, decimal string. */
  receiveAmount: string;
  /** Quote being redeemed. */
  quoteId: string;
  /** 18-digit CLABE — masked or raw; normalized to digits. */
  clabe: string;
  /** Required by the backend `SpeiDestinationDto` (<= 120 chars). */
  beneficiaryName: string;
  /** Optional RFC (<= 13 chars). */
  rfc?: string;
  concept?: string;
  /** Reuse across retries of the same operation. Defaults to a fresh uuid. */
  idempotencyKey?: string;
}

/**
 * Build the CreatePayoutPayload for the México / SPEI corridor. Mirror of `buildBrazilPixPayoutPayload`:
 * `gross_amount` is the quote's converted gross, and the commission comes off it on the backend.
 */
export function buildMexicoSpeiPayoutPayload(input: BuildMexicoSpeiPayoutInput): CreatePayoutPayload {
  const rfc = input.rfc?.trim().toUpperCase();
  return {
    organization_id: input.organizationId,
    rail: PayoutRail.SPEI,
    country: "MEX",
    currency: "MXN",
    gross_amount: input.receiveAmount,
    quote_id: input.quoteId,
    idempotency_key: input.idempotencyKey ?? newIdempotencyKey(),
    ...(input.concept ? { concept: input.concept } : {}),
    destination: {
      rail: PayoutRail.SPEI,
      clabe: input.clabe.replace(/\D/g, ""),
      beneficiary_name: input.beneficiaryName.trim(),
      ...(rfc ? { rfc } : {}),
    },
  };
}

export interface BuildArgentinaCbuCvuPayoutInput {
  organizationId: string;
  /** The quote's converted gross in the corridor currency, decimal string. */
  receiveAmount: string;
  /** Quote being redeemed. */
  quoteId: string;
  /** "CBU" (bank) or "CVU" (wallet) — tolerates lowercase form values. */
  accountIdType: string;
  /** 22-digit CBU/CVU — masked or raw; normalized to digits. */
  accountIdValue: string;
  /** Optional beneficiary CUIT/CUIL — normalized to digits (<= 13 chars). */
  cuitCuil?: string;
  beneficiaryName?: string;
  concept?: string;
  /** Reuse across retries of the same operation. Defaults to a fresh uuid. */
  idempotencyKey?: string;
}

/**
 * Build the CreatePayoutPayload for the Argentina / CBU_CVU corridor from the form + quote. Mirror of
 * `buildBrazilPixPayoutPayload`: `gross_amount` is the quote's converted gross, and the commission comes off
 * it on the backend.
 */
export function buildArgentinaCbuCvuPayoutPayload(
  input: BuildArgentinaCbuCvuPayoutInput,
): CreatePayoutPayload {
  const accountIdType = input.accountIdType.trim().toUpperCase();
  if (accountIdType !== CbuCvuAccountIdType.CBU && accountIdType !== CbuCvuAccountIdType.CVU) {
    throw new Error(`Unsupported CBU/CVU account id type: ${String(input.accountIdType)}`);
  }
  const cuitCuil = input.cuitCuil?.replace(/\D/g, "");
  const beneficiaryName = input.beneficiaryName?.trim();
  return {
    organization_id: input.organizationId,
    rail: PayoutRail.CBU_CVU,
    country: "ARG",
    currency: "ARS",
    gross_amount: input.receiveAmount,
    quote_id: input.quoteId,
    idempotency_key: input.idempotencyKey ?? newIdempotencyKey(),
    ...(input.concept ? { concept: input.concept } : {}),
    destination: {
      rail: PayoutRail.CBU_CVU,
      account_id_type: accountIdType,
      account_id_value: input.accountIdValue.replace(/\D/g, ""),
      ...(cuitCuil ? { cuit_cuil: cuitCuil } : {}),
      ...(beneficiaryName ? { beneficiary_name: beneficiaryName } : {}),
    },
  };
}

/**
 * Normalize a Bre-B key value for the API. The backend forwards `breb_key_value` verbatim to the provider
 * (Kamin resolves it in the DICE directory), so normalize per key type exactly as the input mask does: email
 * lowercased, phone reduced to digits, and documents / alphanumeric / merchant keys trimmed as typed (they
 * are case- and character-sensitive). Shared by the dispersion Bre-B panel and this builder so what the user
 * sees, what is validated and what is sent stay byte-identical.
 */
export function normalizeBrebKeyValue(type: BrebKeyType, value: string): string {
  const trimmed = value.trim();
  if (type === BrebKeyType.EMAIL) return trimmed.toLowerCase();
  if (type === BrebKeyType.PHONE) return trimmed.replace(/\D/g, "");
  return trimmed;
}

/**
 * Bre-B key validity by type, for the dispersion step-2 gate and the recipient panel field. PHONE: Colombian
 * mobile (10 digits starting with 3). EMAIL: standard. ALPHANUM: the "@handle" alias (letters/digits/._- up
 * to 64). NRIC: document number (5–20 alphanumerics). MERCHANT: a merchant code (1–64 chars). Kept next to
 * `normalizeBrebKeyValue` so what is validated and what is sent never drift.
 */
export function isBrebKeyValid(type: BrebKeyType, value: string): boolean {
  const v = value.trim();
  switch (type) {
    case BrebKeyType.PHONE:
      return /^3\d{9}$/.test(v.replace(/\D/g, ""));
    case BrebKeyType.EMAIL:
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.toLowerCase());
    case BrebKeyType.ALPHANUM:
      return /^@?[a-zA-Z0-9._-]{1,63}$/.test(v);
    case BrebKeyType.NRIC:
      return /^[a-zA-Z0-9]{5,20}$/.test(v);
    case BrebKeyType.MERCHANT:
      return v.length >= 1 && v.length <= 64;
    default:
      return false;
  }
}

export interface BuildColombiaBrebPayoutInput {
  organizationId: string;
  /** The quote's converted gross in the corridor currency, decimal string. */
  receiveAmount: string;
  /** Quote being redeemed. */
  quoteId: string;
  /** Bre-B key (alias) type — tolerates lowercase form values. */
  brebKeyType: string;
  /** Bre-B key value — normalized per type before sending. */
  brebKeyValue: string;
  beneficiaryName?: string;
  concept?: string;
  /** Reuse across retries of the same operation. Defaults to a fresh uuid. */
  idempotencyKey?: string;
  /** Raw string of the scanned QR when the payout pays one. */
  brebQrString?: string;
}

/**
 * Build the CreatePayoutPayload for the Colombia / Bre-B corridor from the form + quote. Mirror of
 * `buildArgentinaCbuCvuPayoutPayload`: `gross_amount` is the COP amount the beneficiary receives — align the
 * backend commission config the same way (fees = 0 when the quote already includes it, so net == gross).
 */
export function buildColombiaBrebPayoutPayload(input: BuildColombiaBrebPayoutInput): CreatePayoutPayload {
  const brebKeyType = input.brebKeyType.trim().toUpperCase();
  if (!(Object.values(BrebKeyType) as string[]).includes(brebKeyType)) {
    throw new Error(`Unsupported Bre-B key type: ${String(input.brebKeyType)}`);
  }
  const keyType = brebKeyType as BrebKeyType;
  const beneficiaryName = input.beneficiaryName?.trim();
  return {
    organization_id: input.organizationId,
    rail: PayoutRail.BREB,
    country: "COL",
    currency: "COP",
    gross_amount: input.receiveAmount,
    quote_id: input.quoteId,
    idempotency_key: input.idempotencyKey ?? newIdempotencyKey(),
    ...(input.concept ? { concept: input.concept } : {}),
    destination: {
      rail: PayoutRail.BREB,
      breb_key_type: keyType,
      breb_key_value: normalizeBrebKeyValue(keyType, input.brebKeyValue),
      ...(beneficiaryName ? { beneficiary_name: beneficiaryName } : {}),
    },
    ...(input.brebQrString ? { breb_qr: { qr_string: input.brebQrString } } : {}),
  };
}

export interface BuildColombiaTransfiyaPayoutInput {
  organizationId: string;
  /** The quote's `amounts.receive` (converted gross), decimal string. */
  receiveAmount: string;
  quoteId: string;
  accountType: TransfiyaAccountType;
  accountNumber: string;
  bankCode: string;
  documentType: ColombianDocumentType;
  documentNumber: string;
  beneficiaryName: string;
  concept?: string;
  idempotencyKey?: string;
}

/**
 * Build the CreatePayoutPayload for the Colombia / TRANSFIYA (ACH en línea) corridor: an account-to-account
 * credit to a bank account, addressed by account type + number + participant bank code, with the beneficiary
 * identified by their Colombian document. Unlike Bre-B there is no directory resolution — an unknown account
 * fails at the provider bridge (payout ends FAILED, funds returned).
 */
export function buildColombiaTransfiyaPayoutPayload(
  input: BuildColombiaTransfiyaPayoutInput,
): CreatePayoutPayload {
  return {
    organization_id: input.organizationId,
    rail: PayoutRail.TRANSFIYA,
    country: "COL",
    currency: "COP",
    gross_amount: input.receiveAmount,
    quote_id: input.quoteId,
    idempotency_key: input.idempotencyKey ?? newIdempotencyKey(),
    ...(input.concept ? { concept: input.concept } : {}),
    destination: {
      rail: PayoutRail.TRANSFIYA,
      account_type: input.accountType,
      account_number: input.accountNumber.replace(/\D/g, ""),
      bank_code: input.bankCode,
      document_type: input.documentType,
      document_number: input.documentNumber.replace(/\D/g, ""),
      beneficiary_name: input.beneficiaryName.trim(),
    },
  };
}

export class PaymentsPayoutService {
  private static BASE_URL = "/internal/payments/payouts";

  /**
   * Create + reserve a payout (stays CREATED). Any dashboard user with `payments:write` may call this. The
   * optional `Idempotency-Key` header is sent in addition to the body `idempotency_key` so a network retry is
   * safe.
   */
  static async createPayout(payload: CreatePayoutPayload): Promise<AxiosResponse<PayoutResponse>> {
    return HttpApiService.post<PayoutResponse, CreatePayoutPayload>(this.BASE_URL, payload, {
      headers: { "Idempotency-Key": payload.idempotency_key },
    });
  }

  /**
   * Dispatch a CREATED payout to its provider (executes the payment). IMPORTANT: this endpoint is INTERNAL
   * staff only (`userTypes: [INTERNAL]`). An ORGANIZATION_MEMBER will get 403 — in that case the payout stays
   * CREATED and is dispatched by ops/staff out of band.
   */
  static async dispatchPayout(id: string): Promise<AxiosResponse<PayoutResponse>> {
    return HttpApiService.post<PayoutResponse, Record<string, never>>(`${this.BASE_URL}/${id}/dispatch`, {});
  }

  /** Cancel a not-yet-dispatched payout (releases the reserved funds). */
  static async cancelPayout(id: string, reason?: string): Promise<AxiosResponse<PayoutResponse>> {
    return HttpApiService.post<PayoutResponse, { reason?: string }>(
      `${this.BASE_URL}/${id}/cancel`,
      reason ? { reason } : {},
    );
  }

  static async getPayout(id: string): Promise<AxiosResponse<PayoutResponse>> {
    return HttpApiService.get<PayoutResponse>(`${this.BASE_URL}/${id}`);
  }

  /**
   * List payouts, newest first (`created_at DESC`). Same tenancy rule as the payins list: org-scoped callers
   * always pass `organizationId`; only INTERNAL staff views may omit it to list cross-org.
   */
  static async listPayouts(params: ListPayoutsParams): Promise<AxiosResponse<PaginatedRuntimePayouts>> {
    const query = new URLSearchParams();
    if (params.organizationId) query.set("organization_id", params.organizationId);
    if (params.status) query.set("status", params.status);
    if (params.rail) query.set("rail", params.rail);
    if (params.country) query.set("country", params.country);
    if (params.currency) query.set("currency", params.currency);
    if (params.from) query.set("from", params.from);
    if (params.to) query.set("to", params.to);
    if (params.sourceCurrency) query.set("source_currency", params.sourceCurrency);
    if (params.dateField) query.set("date_field", params.dateField);
    if (params.limit != null) query.set("limit", String(params.limit));
    if (params.skip != null) query.set("skip", String(params.skip));
    const suffix = query.size > 0 ? `?${query}` : "";
    return HttpApiService.get<PaginatedRuntimePayouts>(`${this.BASE_URL}${suffix}`);
  }
}
